import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { User, Mail, Key, Save, Eye, EyeOff, RefreshCw, Shield } from 'lucide-react';

// Helper to get auth config for axios requests (mobile compatibility)
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

export default function SuperAdminProfile() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({ name: '', email: '' });
  
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    setProfile({ name: user.name || '', email: user.email || '' });
  }, [user, navigate]);

  const handleSaveProfile = async () => {
    if (!profile.name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      setSaving(true);
      await axios.patch(`${API}/platform/admin/profile`, { name: profile.name }, getAuthConfig());
      toast.success('Profile updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error('All fields are required');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      setChangingPassword(true);
      await axios.post(`${API}/platform/admin/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, getAuthConfig());
      toast.success('Password changed');
      setShowPasswordDialog(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (!user?.is_super_admin) return null;

  return (
    <SuperAdminLayout backLink="/super-admin">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header - Title on same line */}
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="min-w-0">
            <h1 className="text-lg lg:text-2xl font-bold text-white">My Profile</h1>
            <p className="text-xs lg:text-sm text-zinc-400">Account settings</p>
          </div>
        </div>

        {/* Profile Card */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2 sm:pb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                {user?.name?.[0]?.toUpperCase()}
              </div>
              <div>
                <CardTitle className="text-white text-base sm:text-lg">{user?.name}</CardTitle>
                <CardDescription className="text-zinc-400 flex items-center gap-1 text-xs sm:text-sm">
                  <Shield className="w-3 h-3" />
                  Super Admin
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-zinc-300 text-sm flex items-center gap-2">
                <User className="w-4 h-4" /> Full Name
              </Label>
              <Input
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm"
              />
            </div>
            <div>
              <Label className="text-zinc-300 text-sm flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email
              </Label>
              <Input
                value={profile.email}
                disabled
                className="mt-1 bg-zinc-800 border-zinc-700 text-zinc-400 text-sm"
              />
              <p className="text-xs text-zinc-500 mt-1">Email cannot be changed</p>
            </div>
            <div className="pt-3 border-t border-zinc-800">
              <Button onClick={handleSaveProfile} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto">
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Security Card */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
              <Key className="w-4 h-4 sm:w-5 sm:h-5" />
              Security
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-zinc-400 text-sm mb-4">Keep your account secure by using a strong password.</p>
            <Button variant="outline" onClick={() => setShowPasswordDialog(true)} className="text-zinc-300 w-full sm:w-auto">
              <Key className="w-4 h-4 mr-2" />
              Change Password
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-zinc-300 text-sm">Current Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white text-sm pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-400 h-7 w-7 p-0"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">New Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white text-sm pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-400 h-7 w-7 p-0"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Confirm Password</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)} className="text-zinc-300 w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={handleChangePassword} disabled={changingPassword} className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto">
              {changingPassword ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  );
}
